// ============================================================
// PRTCL Schedule Generation Utility
// Pure functions - no side effects.
// Phase-aware - reads EffectiveSchedule via getEffectiveSchedule.
// ============================================================

import { FrequencyPattern } from '../data/types';
import type {
  Protocol,
  ProtocolComponent,
  DoseLog,
  ScheduledDose,
} from '../data/types';
import { getCurrentPhase, getEffectiveSchedule } from './protocol';
import { groupComponents, unitsForDose, type ComponentGroup } from './calculations';

/**
 * The local calendar date a log settles against - i.e. which scheduled
 * occurrence it completes.
 *
 * `scheduledTime` (when set) is the occurrence the log satisfies.
 * `loggedTime` is only when the user pressed Log. The two differ whenever a
 * missed dose is logged on a later day, and matching purely on `loggedTime`
 * meant that dose could never be reconciled: the day it was due stayed
 * pending forever in "Missed this week", while the day it was entered showed
 * a dose that was not actually scheduled then.
 *
 * Falls back to `loggedTime` for ad-hoc logs, which have no scheduled
 * occurrence, and for logs written before this field was populated - so
 * existing history keeps matching exactly as it did.
 */
function logSettlementDate(log: DoseLog): string {
  if (log.scheduledTime) {
    const scheduled = new Date(log.scheduledTime);
    // Defensive: never let an unparseable value knock out a real log.
    if (!Number.isNaN(scheduled.getTime())) return formatDateStr(scheduled);
  }
  return formatDateStr(new Date(log.loggedTime));
}

/**
 * Offset a date by a delay amount.
 */
function addDelay(date: Date, delay: { value: number; unit: 'days' | 'weeks' | 'months' }): Date {
  const result = new Date(date);
  switch (delay.unit) {
    case 'days': result.setDate(result.getDate() + delay.value); break;
    case 'weeks': result.setDate(result.getDate() + delay.value * 7); break;
    case 'months': result.setMonth(result.getMonth() + delay.value); break;
  }
  return result;
}

/**
 * Get the effective start date for a component, accounting for startDelay.
 */
export function getComponentStartDate(component: ProtocolComponent, protocol: Protocol): Date {
  const start = parseLocalDate(protocol.startDate);
  return component.startDelay ? addDelay(start, component.startDelay) : start;
}

/**
 * Determine whether a component is due on a given date.
 * Uses the CURRENT phase's effective schedule.
 */
export function isComponentDueOnDate(
  component: ProtocolComponent,
  protocol: Protocol,
  date: Date,
): boolean {
  const phase = getCurrentPhase(component, date);
  const schedule = getEffectiveSchedule(component, phase);

  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const start = getComponentStartDate(component, protocol);

  if (target < start) return false;

  const { frequency, selectedDays } = schedule;

  switch (frequency) {
    case FrequencyPattern.DAILY:
      return true;

    case FrequencyPattern.SPECIFIC_DAYS:
      return Array.isArray(selectedDays) && selectedDays.includes(target.getDay());

    case FrequencyPattern.EOD: {
      const diffMs = target.getTime() - start.getTime();
      const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
      return diffDays % 2 === 0;
    }

    case FrequencyPattern.WEEKLY:
      if (Array.isArray(selectedDays) && selectedDays.length === 1) {
        return target.getDay() === selectedDays[0];
      }
      return target.getDay() === start.getDay();

    default:
      return true;
  }
}

/**
 * THE schedule entry point (v3, E2/E8): one ScheduledDose per
 * administration. Combined groups (components sharing a mixGroupId)
 * emit ONE dose per time slot carrying the group display name, the
 * shared drawUnits, and the per-component breakdown; singles emit
 * exactly what generateComponentSchedule always emitted.
 *
 * Pass the full log list - per-component filtering happens inside
 * (ad-hoc logs with null componentId never match).
 */
export function generateProtocolSchedule(
  protocol: Protocol,
  date: Date,
  logs: DoseLog[],
): ScheduledDose[] {
  if (!protocol.isActive) return [];
  const out: ScheduledDose[] = [];
  for (const group of groupComponents(protocol)) {
    if (group.isCombined) {
      out.push(...generateGroupSchedule(group, protocol, date, logs));
    } else {
      out.push(...generateComponentSchedule(group.components[0], protocol, date, logs));
    }
  }
  return out;
}

/**
 * One combined group -> one ScheduledDose per time slot. The group's
 * FIRST component is the lead: its schedule/phase drives dueness (the
 * wizard asks the schedule once per mixture, so all members match),
 * and its logs drive completion (one administration writes one row
 * per component, so lead rows count administrations).
 */
function generateGroupSchedule(
  group: ComponentGroup,
  protocol: Protocol,
  date: Date,
  logs: DoseLog[],
): ScheduledDose[] {
  const lead = group.components[0];
  if (!isComponentDueOnDate(lead, protocol, date)) return [];

  const leadPhase = getCurrentPhase(lead, date);
  const schedule = getEffectiveSchedule(lead, leadPhase);
  const times = schedule.timesOfDay.length > 0 ? schedule.timesOfDay : ['08:00'];

  const dateStr = formatDateStr(date);
  const leadLogs = logs.filter(
    (l) => l.componentId === lead.id && logSettlementDate(l) === dateStr,
  );

  const breakdown = group.components.map((c) => {
    const phase = getCurrentPhase(c, date);
    return {
      componentId: c.id,
      phaseId: phase.id,
      peptideId: c.peptideId,
      peptideName: c.peptideName,
      doseMcg: phase.doseMcg,
    };
  });
  const totalMcg = breakdown.reduce((sum, b) => sum + b.doseMcg, 0);
  // v3-written mixes always carry drawUnits; the fallback re-derives
  // from the stored mixture concentration for defensive completeness.
  const units = lead.drawUnits ?? unitsForDose(totalMcg, lead.concentrationMcgPerUnit);

  return times.map((time, idx) => {
    const matchedLog = leadLogs[idx];
    return {
      id: `sched-${group.mixGroupId}-${leadPhase.id}-${time}`,
      protocolId: protocol.id,
      componentId: lead.id,
      phaseId: leadPhase.id,
      peptideId: lead.peptideId,
      peptideName: group.displayName,
      protocolName: protocol.name,
      doseMcg: totalMcg,
      units,
      scheduledTime: time,
      isCompleted: !!matchedLog,
      doseLogId: matchedLog?.id,
      mealTiming: schedule.mealTiming ?? null,
      mixGroupId: group.mixGroupId ?? undefined,
      groupComponents: breakdown,
    };
  });
}

/**
 * Generate ScheduledDose entries for a component on a given date.
 * Module-private since v3 (E8): all consumers go through
 * generateProtocolSchedule so combined groups can never leak back in
 * as per-component cards.
 */
function generateComponentSchedule(
  component: ProtocolComponent,
  protocol: Protocol,
  date: Date,
  logs: DoseLog[],
): ScheduledDose[] {
  if (!protocol.isActive) return [];
  if (!isComponentDueOnDate(component, protocol, date)) return [];

  const phase = getCurrentPhase(component, date);
  const schedule = getEffectiveSchedule(component, phase);
  const times = schedule.timesOfDay.length > 0 ? schedule.timesOfDay : ['08:00'];

  // Compare the LOCAL calendar date each log settles against with the local
  // date we're generating for. Two traps live here, both already paid for:
  // using `startsWith` on the ISO UTC string was wrong (a dose logged 9pm
  // local Apr 4 EST stores as "2026-04-05T01:00:00Z" and never matched a scan
  // for "2026-04-04"), and matching on loggedTime alone was wrong for a
  // missed dose logged on a later day - see logSettlementDate.
  const dateStr = formatDateStr(date);
  const todayLogs = logs.filter(
    (l) => l.componentId === component.id && logSettlementDate(l) === dateStr,
  );

  return times.map((time, idx) => {
    const matchedLog = todayLogs[idx];
    return {
      id: `sched-${component.id}-${phase.id}-${time}`,
      protocolId: protocol.id,
      componentId: component.id,
      phaseId: phase.id,
      peptideId: component.peptideId,
      peptideName: component.peptideName,
      protocolName: protocol.name,
      doseMcg: phase.doseMcg,
      units: unitsForDose(phase.doseMcg, component.concentrationMcgPerUnit),
      scheduledTime: time,
      isCompleted: !!matchedLog,
      doseLogId: matchedLog?.id,
      mealTiming: schedule.mealTiming ?? null,
    };
  });
}

/**
 * Find the next date (from and including fromDate) when a component is due.
 */
export function getNextScheduledDateForComponent(
  component: ProtocolComponent,
  protocol: Protocol,
  fromDate: Date,
): Date | null {
  for (let i = 0; i < 14; i++) {
    const check = new Date(fromDate);
    check.setDate(check.getDate() + i);
    if (isComponentDueOnDate(component, protocol, check)) {
      return check;
    }
  }
  return null;
}

export interface NextUncompletedDose {
  date: Date;
  time: string; // 'HH:MM'
}

/**
 * Find the next scheduled dose for a component that is NOT yet completed
 * by an existing dose log. Walks forward from fromDate (inclusive) up to
 * 14 days; on each scheduled day uses generateComponentSchedule to honor
 * the same logs-by-local-date + index-match completion rule the dashboard
 * uses, so an "early" log (e.g., logged at 7am for a 9am slot) correctly
 * marks the slot complete and the next dose advances to the next slot or
 * day.
 */
export function getNextUncompletedDoseForComponent(
  component: ProtocolComponent,
  protocol: Protocol,
  fromDate: Date,
  logs: DoseLog[],
): NextUncompletedDose | null {
  if (!protocol.isActive) return null;
  for (let i = 0; i < 14; i++) {
    const check = new Date(fromDate);
    check.setDate(check.getDate() + i);
    const slots = generateComponentSchedule(component, protocol, check, logs);
    const pending = slots.find((s) => !s.isCompleted);
    if (pending) {
      return { date: check, time: pending.scheduledTime };
    }
  }
  return null;
}

/**
 * Get a list of which days in the next N days are active for a component.
 */
export function getComponentSchedulePreview(
  component: ProtocolComponent,
  protocol: Protocol,
  startFrom: Date,
  days: number = 7,
): { date: Date; dayOfWeek: number; isActive: boolean }[] {
  const preview: { date: Date; dayOfWeek: number; isActive: boolean }[] = [];
  for (let i = 0; i < days; i++) {
    const d = new Date(startFrom);
    d.setDate(d.getDate() + i);
    preview.push({
      date: d,
      dayOfWeek: d.getDay(),
      isActive: isComponentDueOnDate(component, protocol, d),
    });
  }
  return preview;
}

// ---- Display helpers (unchanged from before) ----

export function formatTime(time: string): string {
  const [hStr, mStr] = time.split(':');
  const hour = parseInt(hStr, 10);
  const minute = mStr || '00';
  const period = hour >= 12 ? 'PM' : 'AM';
  const display = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${display}:${minute} ${period}`;
}

export function getMealTimingLabel(
  mealTiming: 'fasted' | 'after_meal' | 'anytime' | null | undefined,
): string | null {
  if (!mealTiming) return null;
  if (mealTiming === 'fasted') return 'Fasted';
  if (mealTiming === 'after_meal') return 'After meal';
  if (mealTiming === 'anytime') return 'Anytime';
  return null;
}

// ---- Helpers ----

/**
 * Parse an ISO date string (YYYY-MM-DD) as a local-timezone Date at midnight.
 * Exported for reminders.ts drift/dueness calculations to stay consistent.
 */
export function parseLocalDate(isoDate: string): Date {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Format a Date as a local-timezone YYYY-MM-DD string.
 * Do NOT use toISOString() for day bucketing - it converts to UTC and
 * shifts dates across timezone boundaries (e.g. a dose logged at 9pm EST
 * on Apr 4 would key as Apr 5).
 * Exported for day-bucketing consumers (HistoryPage).
 */
export function formatDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * How a day's schedule actually resolved: doses taken vs doses skipped.
 *
 * A skip closes a slot exactly as a logged dose does, so "is the carousel
 * empty?" cannot tell the two apart. The dashboards used that emptiness to
 * say "All done for today." next to a green check, over a list of skips -
 * which claims the user took doses they explicitly did not take. Tester
 * feedback flagged it on 2026-08-26.
 *
 * Unresolved (still pending) slots are counted in neither bucket.
 */
export function summarizeDayOutcome(
  schedule: ScheduledDose[],
  logs: DoseLog[],
): { taken: number; skipped: number } {
  const logById = new Map(logs.map((l) => [l.id, l]));
  let taken = 0;
  let skipped = 0;
  for (const slot of schedule) {
    if (!slot.isCompleted) continue;
    const log = slot.doseLogId ? logById.get(slot.doseLogId) : undefined;
    if (log?.skipped) skipped += 1;
    else taken += 1;
  }
  return { taken, skipped };
}
